import { App, Form, Radio, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  CustomActionButtons,
  CustomButton,
  CustomInput,
  CustomModal,
  CustomTooltip,
  CustomVirtualScroll,
} from "sipli-fleet-core/components";
import {
  useMutationCloneMappingPreset,
  useMutationExportMappingPreset,
  useMutationUseMappingPreset,
  useQueryGetMappingPresetByDeviceType,
  useQueryGetMappingPresetNames,
} from "src/queries/mappingPreset.queries";
import type { MappingPresetRecord } from "src/services/mappingPresetService";
import { cn } from "../../../../../../sipli-fleet-core/utils/cn";
import { getPresetName, getPresetRecords } from "./mappingPreset.helpers";

interface CreatePresetModalProps {
  open: boolean;
  mode: "create" | "clone";
  deviceMappingId?: string | number;
  modelId?: string | number;
  cloneRecord?: MappingPresetRecord | null;
  onClose: () => void;
}

interface UsePresetModalProps {
  open: boolean;
  unitId?: string | number;
  modelId?: string | number;
  onClose: () => void;
}

interface MappingPresetActionsProps {
  unitId: string | number;
  modelId?: string | number;
  deviceMappingId?: string | number;
  hasMappings: boolean;
}

export const CreatePresetModal = ({
  open,
  mode,
  deviceMappingId,
  modelId,
  cloneRecord,
  onClose,
}: CreatePresetModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<{ name: string }>();
  const presetNamesQuery = useQueryGetMappingPresetNames(open);
  const exportMutation = useMutationExportMappingPreset();
  const cloneMutation = useMutationCloneMappingPreset();
  const isClone = mode === "clone";
  const nameValue = Form.useWatch("name", form) ?? "";
  const existingNames = useMemo(() => {
    const records = presetNamesQuery.data?.data.response ?? [];
    const matchedRecord = records.find((item) => String(item.deviceType) === String(modelId));

    return (matchedRecord?.setNames ?? []).map((name) => name.toLowerCase());
  }, [modelId, presetNamesQuery.data?.data.response]);
  const isDuplicate = Boolean(nameValue) && existingNames.includes(nameValue.toLowerCase());
  const isLoading = exportMutation.isPending || cloneMutation.isPending;

  useEffect(() => {
    if (open) form.resetFields();
  }, [form, open]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const trimmedName = values.name.trim();

    if (isDuplicate) {
      form.setFields([
        {
          name: "name",
          errors: [
            t("preset_name_already_exists", {
              defaultValue: "This preset name already exists.",
            }),
          ],
        },
      ]);
      return;
    }

    try {
      if (isClone && cloneRecord) {
        await cloneMutation.mutateAsync({
          id: cloneRecord.id,
          name: trimmedName,
        });
        message.success(
          t("preset_cloned_successfully", {
            defaultValue: "Preset Cloned Successfully!",
          })
        );
      } else if (deviceMappingId) {
        await exportMutation.mutateAsync({
          deviceMappingId,
          name: trimmedName,
        });
        message.success(
          t("preset_created_successfully", {
            defaultValue: "Preset Created Successfully!",
          })
        );
      }
      form.resetFields();
      onClose();
    } catch {
      message.error(t("error"));
    }
  };

  return (
    <CustomModal
      open={open}
      title={
        isClone
          ? t("clonePresets", { defaultValue: "Clone Presets" })
          : t("createPresets", { defaultValue: "Create Presets" })
      }
      onCancel={onClose}
      showFooter={false}
      width={560}
      destroyOnHidden
      centered
    >
      <Form form={form} layout="vertical" requiredMark={false} className="pt-2">
        <Form.Item
          name="name"
          label={t("name")}
          validateStatus={isDuplicate ? "error" : undefined}
          help={
            isDuplicate
              ? t("preset_name_already_exists", {
                  defaultValue: "This preset name already exists.",
                })
              : undefined
          }
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("preset_name_is_required"),
            },
          ]}
        >
          <CustomInput placeholder={t("enter_name")} className="common-input" />
        </Form.Item>
      </Form>
      <CustomActionButtons
        okBtnLabel={t("save")}
        cancelBtnLabel={t("close")}
        loading={isLoading}
        // disabled={isDuplicate || (!isClone && !deviceMappingId)}
        disabled={isDuplicate || (!isClone && !deviceMappingId)}
        onOk={handleSubmit}
        onCancel={onClose}
        className="mt-6"
      />
    </CustomModal>
  );
};

const UsePresetModal = ({ open, unitId, modelId, onClose }: UsePresetModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [selectedPreset, setSelectedPreset] = useState<MappingPresetRecord | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const presetsQuery = useQueryGetMappingPresetByDeviceType(modelId, {
    enabled: open && !!modelId,
  });

  const usePresetMutation = useMutationUseMappingPreset();

  const presets = useMemo(() => getPresetRecords(presetsQuery.data), [presetsQuery.data]);

  useEffect(() => {
    if (open) {
      setSelectedPreset(null);
      setConfirmOpen(false);
    }
  }, [open]);

  const handleUsePreset = async (preset: MappingPresetRecord | null) => {
    if (!unitId || !preset) return;

    try {
      await usePresetMutation.mutateAsync({
        unitId,
        presetId: preset.id,
      });

      message.success(
        t("use_preset_applied_successfully", {
          defaultValue: "Use Preset Applied Successfully!",
        })
      );

      setConfirmOpen(false);
      onClose();
    } catch {
      message.error(t("error"));
    }
  };

  const handleOpenConfirm = () => {
    if (!selectedPreset) return;
    setConfirmOpen(true);
  };

  const confirmationText = t("use_preset_confirmation", {
    defaultValue: "Are you sure you want to use this preset ?",
  });

  return (
    <>
      <CustomModal
        open={open}
        title={t("use_presets", { defaultValue: "Use Presets" })}
        width={560}
        onCancel={onClose}
        onOk={handleOpenConfirm}
        okText={t("submit", { defaultValue: "Submit" })}
        cancelText={t("close", { defaultValue: "Close" })}
        okButtonProps={{ disabled: !selectedPreset }}
        destroyOnHidden
        centered
      >
        {presetsQuery.isLoading || presetsQuery.isFetching ? (
          <div className="flex h-[170px] items-center justify-center">
            <Spin />
          </div>
        ) : presets.length ? (
          <CustomVirtualScroll<MappingPresetRecord>
            items={presets}
            height={200}
            width="100%"
            renderItem={(index, preset) => (
              <button
                key={preset.id}
                type="button"
                className={cn(
                  "flex w-full cursor-pointer items-center gap-1.5 border-0 border-b border-solid border-[#e0e0e0] bg-white px-2 py-3 hover:bg-black/[0.04]",
                  index === presets.length - 1 && "border-b-0"
                )}
                onClick={() =>
                  setSelectedPreset((previous) => (previous?.id === preset.id ? null : preset))
                }
              >
                <Radio checked={selectedPreset?.id === preset.id} />
                <span>{getPresetName(preset)}</span>
              </button>
            )}
          />
        ) : (
          <div className="py-10 text-center text-gray9">
            {t("data_not_found", { defaultValue: "Data Not Found!" })}
          </div>
        )}
      </CustomModal>

      <CustomModal
        open={confirmOpen}
        width={430}
        onCancel={() => setConfirmOpen(false)}
        showFooter={false}
        destroyOnHidden
        centered
        zIndex={2000}
      >
        <div className="flex flex-col items-center pt-2 pb-0 text-center">
          <div className="mb-4 flex h-[33px] w-[33px] items-center justify-center rounded-full bg-[#FAAD14] text-[24px] font-bold leading-none text-white">
            !
          </div>

          <div className="text-[18px] font-semibold leading-[28px] text-gray10">
            {confirmationText}
          </div>

          <CustomActionButtons
            cancelBtnLabel={t("no", { defaultValue: "No" })}
            loading={usePresetMutation.isPending}
            onCancel={() => setConfirmOpen(false)}
            okBtnLabel={t("yes", { defaultValue: "Yes" })}
            onOk={() => handleUsePreset(selectedPreset)}
            className="mt-5"
          />
        </div>
      </CustomModal>
    </>
  );
};

const MappingPresetActions = ({
  unitId,
  modelId,
  deviceMappingId,
  hasMappings,
}: MappingPresetActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId, vehicleId } = useParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [useOpen, setUseOpen] = useState(false);

  return (
    <>
      <CustomButton
        type="secondary"
        buttonSize="medium"
        label={t("manage_presets", { defaultValue: "Manage presets" })}
        onClick={() => navigate(`/customers/${clientId}/${unitId}/${vehicleId}/presets`)}
      />
      <CustomTooltip
        title={
          !hasMappings || !deviceMappingId
            ? t("no_mappings_found_add_a_mapping_before_creating_a_preset")
            : ""
        }
      >
        <CustomButton
          type="primary"
          buttonSize="medium"
          label={t("create_preset", { defaultValue: "Create preset" })}
          className="!text-gray2 px-6"
          disabled={!hasMappings || !deviceMappingId}
          onClick={() => setCreateOpen(true)}
        />
      </CustomTooltip>
      <CustomButton
        type="primary"
        buttonSize="medium"
        label={t("use_preset", { defaultValue: "Use preset" })}
        className="!text-gray2 px-6"
        disabled={!modelId}
        onClick={() => setUseOpen(true)}
      />
      <CreatePresetModal
        open={createOpen}
        mode="create"
        deviceMappingId={deviceMappingId}
        modelId={modelId}
        onClose={() => setCreateOpen(false)}
      />
      <UsePresetModal
        open={useOpen}
        unitId={unitId}
        modelId={modelId}
        onClose={() => setUseOpen(false)}
      />
    </>
  );
};

export default MappingPresetActions;
